import { ApolloCache, FetchResult, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import {
  evictCache,
  RdProjectAllocationPercentInput,
  UpdateRdExpenseProjectAllocationsMutation,
  UpdateRdExpenseProjectAllocationsMutationVariables,
  UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS,
} from '@neo.tax/shared';

type UseUpdateRdExpenseProjectAllocations = {
  updateRdExpenseProjectAllocations: (
    rdExpenseId: string,
    rdProjectAllocationPercents: Array<RdProjectAllocationPercentInput>,
  ) => Promise<FetchResult<UpdateRdExpenseProjectAllocationsMutation>>;
} & MutationResult<UpdateRdExpenseProjectAllocationsMutation>;

const UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS_REQUEST_KEY = 'UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS_REQUEST_KEY';

export const useUpdateRdExpenseProjectAllocations = ({
  onSuccess,
}: {
  onSuccess?: () => void;
}): UseUpdateRdExpenseProjectAllocations => {
  const [updateRdExpenseProjectAllocationsMutation, updateRdExpenseProjectAllocationsResult] = useMutation<
    UpdateRdExpenseProjectAllocationsMutation,
    UpdateRdExpenseProjectAllocationsMutationVariables
  >(UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS, {
    context: {
      serializationKey: UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS_REQUEST_KEY,
      debounceKey: UPDATE_RD_EXPENSE_PROJECT_ALLOCATIONS_REQUEST_KEY,
      errorToast: {
        title: 'Failed to update percentages',
        description: 'An unexpected error happened while updating. Please try again.',
      },
      successToast: {
        title: 'Successfully updated percentages',
      },
    },
  });

  const updateRdExpenseProjectAllocations = useCallback(
    (
      rdExpenseId: string,
      rdProjectAllocationPercents: Array<RdProjectAllocationPercentInput>,
    ): Promise<FetchResult<UpdateRdExpenseProjectAllocationsMutation>> =>
      updateRdExpenseProjectAllocationsMutation({
        variables: {
          rdExpenseId,
          updateRdExpenseProjectAllocationsInput: { rdProjectAllocationPercents },
        },
        onCompleted: onSuccess,
        update: (cache: ApolloCache<UpdateRdExpenseProjectAllocationsMutation>): void => {
          //invalidate cache
          evictCache(cache, [
            'getPayrollQreSummary',
            'getAccountingQreSummary',
            'getRdProjectTeamMembers',
            'getRdExpenseProjectAllocations',
          ]);
        },
      }),
    [onSuccess, updateRdExpenseProjectAllocationsMutation],
  );
  return { updateRdExpenseProjectAllocations, ...updateRdExpenseProjectAllocationsResult };
};
